/**
 * Certifications Page
 *
 * Lists all available domain exams, user's credentials, and allows
 * starting/continuing certification exams.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  useGetAllExamsQuery,
  useGetMyCredentialsQuery,
  useGetMyVerifiedCredentialsQuery,
  useGetMyExamProgressQuery,
  useStartExamMutation,
  useCompleteExamMutation,
  type CompleteExamResponse,
  type DomainExamDefinition,
  type UserDomainCredential,
  type DomainKey,
  type DomainCategory,
  type DomainExamStatus,
} from '../store/api/credentialsApi';
import { VerifiedBadge } from '../components/credentials/VerifiedBadge';
import { LoadErrorNotice } from '../components/LoadErrorNotice';
import { useConversationEngineSwitch } from '../components/ConversationEngineOff';

/** The exam is a graded conversation; with the engine off it cannot run, so it is not started. */
export const EXAM_ENGINE_OFF_MESSAGE =
  'Conversation-based exams are not switched on for this BeHeard environment, so this exam cannot be started.';
import { useListScenariosQuery, type Scenario } from '../store/api/scenarioApi';
import {
  ExamProgressPanel,
  ExamScenarioList,
  NO_EXAM_SCENARIO_MESSAGE,
} from '../components/flows/FlowOutcomes';
import { examConversationUrl, scenariosForExam } from '../utils/flowLinks';

// ============================================
// STYLES & COLORS
// ============================================

const CATEGORY_COLORS: Record<DomainCategory, { bg: string; text: string; border: string }> = {
  real_estate: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  tech_sales: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  investing: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  ecommerce: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  coaching: { bg: 'bg-pink-50', text: 'text-pink-700', border: 'border-pink-200' },
};

const STATUS_COLORS: Record<DomainExamStatus, { bg: string; text: string; badge: string }> = {
  not_started: { bg: 'bg-gray-50', text: 'text-gray-600', badge: 'bg-gray-100 text-gray-600' },
  in_progress: { bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  passed: { bg: 'bg-green-50', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  failed: { bg: 'bg-red-50', text: 'text-red-600', badge: 'bg-red-100 text-red-600' },
};

// ============================================
// COMPONENTS
// ============================================

function ExamCard({
  exam,
  credential,
  verificationHash,
  onStart,
  onContinue,
  isStarting,
  hasScenario,
}: {
  exam: DomainExamDefinition;
  credential?: UserDomainCredential;
  verificationHash?: string;
  onStart: (domainKey: DomainKey) => void;
  onContinue: (domainKey: DomainKey) => void;
  isStarting: boolean;
  /** False when no scenario is authored for this exam: nothing to start. */
  hasScenario: boolean;
}) {
  const categoryColors = CATEGORY_COLORS[exam.category];
  const status: DomainExamStatus = credential?.status || 'not_started';
  const statusColors = STATUS_COLORS[status];

  return (
    <div
      className={`rounded-xl border p-5 transition-all hover:shadow-md ${categoryColors.border} ${categoryColors.bg}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">{exam.label}</h3>
            {verificationHash && <VerifiedBadge verificationHash={verificationHash} showLink />}
          </div>
          <span
            className={`inline-block px-2 py-0.5 text-xs rounded-full mt-1 ${categoryColors.text} bg-white bg-opacity-60`}
          >
            {exam.category.replace('_', ' ')}
          </span>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusColors.badge}`}>
          {status === 'not_started'
            ? 'Not Started'
            : status === 'in_progress'
            ? 'In Progress'
            : status === 'passed'
            ? `Passed (${credential?.overallScore}%)`
            : `Failed (${credential?.overallScore}%)`}
        </span>
      </div>

      <p className="text-sm text-gray-600 mb-4">{exam.description}</p>

      <div className="grid grid-cols-2 gap-3 text-sm text-gray-500 mb-4">
        <div className="flex items-center gap-2">
          <ClockIcon className="w-4 h-4" />
          <span>{exam.minSessions} sessions min</span>
        </div>
        <div className="flex items-center gap-2">
          <TargetIcon className="w-4 h-4" />
          <span>{exam.passingScoreOverall}% to pass</span>
        </div>
        <div className="flex items-center gap-2 col-span-2">
          <BookIcon className="w-4 h-4" />
          <span>{exam.recommendedExperience}</span>
        </div>
      </div>

      {!hasScenario && (status === 'not_started' || status === 'failed') && (
        <p className="mb-2 rounded-lg bg-white/70 p-2 text-sm text-gray-600" data-testid="exam-card-no-scenario">
          {NO_EXAM_SCENARIO_MESSAGE}
        </p>
      )}

      <div className="flex gap-2">
        {status === 'not_started' && hasScenario && (
          <button
            onClick={() => onStart(exam.key)}
            disabled={isStarting}
            className="flex-1 btn-primary py-2 text-sm disabled:opacity-50"
          >
            {isStarting ? 'Starting...' : 'Start Certification'}
          </button>
        )}
        {status === 'in_progress' && (
          <button
            onClick={() => onContinue(exam.key)}
            className="flex-1 btn-primary py-2 text-sm"
          >
            Continue Exam
          </button>
        )}
        {status === 'failed' && hasScenario && (
          <button
            onClick={() => onStart(exam.key)}
            disabled={isStarting}
            className="flex-1 btn-secondary py-2 text-sm disabled:opacity-50"
          >
            {isStarting ? 'Starting...' : 'Retry Exam'}
          </button>
        )}
        {status === 'passed' && (
          <div className="flex-1 flex items-center justify-center gap-2 py-2 text-green-600">
            <CheckCircleIcon className="w-5 h-5" />
            <span className="font-medium">Certified</span>
          </div>
        )}
      </div>

      {credential && credential.attemptsCount > 0 && (
        <p className="text-xs text-gray-400 text-center mt-2">
          {credential.attemptsCount} attempt{credential.attemptsCount > 1 ? 's' : ''}
          {credential.lastCompletedAt && (
            <> · Last: {new Date(credential.lastCompletedAt).toLocaleDateString()}</>
          )}
        </p>
      )}
    </div>
  );
}

export function ExamDetailModal({
  exam,
  onClose,
  onStart,
  isStarting,
  hasScenario,
  startError,
  engineOff = false,
}: {
  exam: DomainExamDefinition;
  onClose: () => void;
  onStart: () => void;
  isStarting: boolean;
  hasScenario: boolean;
  startError: string | null;
  /** The server's conversation engine is switched off: the graded conversation cannot run. */
  engineOff?: boolean;
}) {
  const categoryColors = CATEGORY_COLORS[exam.category];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full">
          <div className={`p-6 rounded-t-xl ${categoryColors.bg}`}>
            <div className="flex items-start justify-between">
              <div>
                <span
                  className={`inline-block px-2 py-0.5 text-xs rounded-full ${categoryColors.text} bg-white bg-opacity-60 mb-2`}
                >
                  {exam.category.replace('_', ' ')}
                </span>
                <h2 className="text-xl font-bold text-gray-900">{exam.label}</h2>
              </div>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700"
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6">{exam.description}</p>

            <div className="space-y-4 mb-6">
              <h3 className="font-medium text-gray-900">Exam Requirements</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600">
                    {exam.passingScoreOverall}%
                  </div>
                  <div className="text-sm text-gray-500">Passing Score</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-2xl font-bold text-primary-600">
                    {exam.minSessions}
                  </div>
                  <div className="text-sm text-gray-500">Min Sessions</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                  <div className="text-lg font-semibold text-gray-700">
                    {Math.floor(exam.minConversationLengthSeconds / 60)} min
                  </div>
                  <div className="text-sm text-gray-500">Min Conversation Time</div>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Recommended Experience</h3>
              <p className="text-sm text-gray-600">{exam.recommendedExperience}</p>
            </div>

            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">What to Expect</h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Complete {exam.minSessions}+ conversation-based assessments</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Demonstrate expertise in real-world scenarios</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Receive detailed feedback on your performance</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>Earn a shareable credential upon passing</span>
                </li>
              </ul>
            </div>

            {!hasScenario && (
              <p className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700" data-testid="exam-modal-no-scenario">
                {NO_EXAM_SCENARIO_MESSAGE}
              </p>
            )}
            {hasScenario && engineOff && (
              <p className="mb-4 rounded-lg bg-gray-50 p-3 text-sm text-gray-700" data-testid="exam-modal-engine-off">
                {EXAM_ENGINE_OFF_MESSAGE}
              </p>
            )}
            {startError && (
              <p className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">{startError}</p>
            )}

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 btn-secondary">
                {hasScenario ? 'Cancel' : 'Close'}
              </button>
              {hasScenario && (
                <button
                  onClick={onStart}
                  disabled={isStarting || engineOff}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {isStarting ? 'Starting...' : 'Begin Exam'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// MAIN PAGE
// ============================================

export function CertificationsPage() {
  const navigate = useNavigate();
  const { domainKey: urlDomainKey } = useParams<{ domainKey: string }>();
  const engine = useConversationEngineSwitch();

  const {
    data: allExams,
    isLoading: isLoadingExams,
    isError: examsFailed,
    error: examsError,
    refetch: refetchExams,
  } = useGetAllExamsQuery();
  const {
    data: myCredentials,
    isLoading: isLoadingCredentials,
    isError: credentialsFailed,
    error: credentialsError,
    refetch: refetchCredentials,
  } = useGetMyCredentialsQuery();
  const { data: myVerifiedCredentials = [] } = useGetMyVerifiedCredentialsQuery();

  const [startExam, { isLoading: isStarting }] = useStartExamMutation();

  const [selectedExam, setSelectedExam] = useState<DomainExamDefinition | null>(null);
  const [activeCategory, setActiveCategory] = useState<DomainCategory | 'all'>('all');
  /** The exam whose attempt is open on this page (started or continued). */
  const [activeExamKey, setActiveExamKey] = useState<DomainKey | null>(null);
  const [startError, setStartError] = useState<string | null>(null);

  // The scenarios authored for ANY exam, in one request: scenarios whose tags
  // include one of the exam definitions' relatedScenarioKeys. The server
  // applies its normal visibility rules; nothing here is client-authored.
  const allScenarioKeys = useMemo(
    () => [...new Set((allExams ?? []).flatMap((e) => e.relatedScenarioKeys))].sort(),
    [allExams]
  );
  const { data: taggedScenarioPage, isLoading: isLoadingScenarios } = useListScenariosQuery(
    { tags: allScenarioKeys, limit: 100 },
    { skip: allScenarioKeys.length === 0 }
  );
  const taggedScenarios: Scenario[] = taggedScenarioPage?.scenarios ?? [];
  const examScenarios = (exam: DomainExamDefinition) => scenariosForExam(exam, taggedScenarios);

  const isLoading = isLoadingExams || isLoadingCredentials;

  // Get credential for a domain
  const getCredential = (domainKey: DomainKey): UserDomainCredential | undefined => {
    return myCredentials?.find((c) => c.domainKey === domainKey);
  };

  // Filter exams by category
  const filteredExams = allExams?.filter(
    (exam) => activeCategory === 'all' || exam.category === activeCategory
  );

  // Group by status for summary
  const passedCount = myCredentials?.filter((c) => c.status === 'passed').length || 0;
  const inProgressCount = myCredentials?.filter((c) => c.status === 'in_progress').length || 0;

  /**
   * Begin (or resume — the server returns the open attempt if there is one)
   * the exam attempt, then go straight to a graded Testing conversation on a
   * scenario authored for this exam. When that conversation is finalized,
   * CoachingPage links it to this attempt (examDomain).
   */
  const handleStartExam = async (domainKey: DomainKey) => {
    const exam = allExams?.find((e) => e.key === domainKey);
    if (!exam) return;
    const scenarios = examScenarios(exam);
    if (scenarios.length === 0) return; // nothing to take: the card says so
    if (engine.off) return; // no graded conversation can run: the modal says so
    setStartError(null);
    try {
      await startExam(domainKey).unwrap();
      setSelectedExam(null);
      navigate(examConversationUrl(scenarios[0].id, domainKey));
    } catch (error) {
      console.error('Failed to start exam:', error);
      setStartError('The exam could not be started right now. Please try again.');
    }
  };

  /** An in-progress exam: show its progress and the scenarios to take next. */
  const handleContinueExam = (domainKey: DomainKey) => {
    setSelectedExam(null);
    setActiveExamKey(domainKey);
  };

  // Direct link (/certifications/:domainKey): an exam already in progress
  // opens its progress panel; any other opens the detail modal.
  useEffect(() => {
    if (!urlDomainKey || !allExams || isLoadingCredentials) return;
    const exam = allExams.find((e) => e.key === urlDomainKey);
    if (!exam) return;
    if (myCredentials?.find((c) => c.domainKey === exam.key)?.status === 'in_progress') {
      setActiveExamKey(exam.key);
    } else {
      setSelectedExam((current) => current ?? exam);
    }
  }, [urlDomainKey, allExams, myCredentials, isLoadingCredentials]);

  const activeExam = activeExamKey ? allExams?.find((e) => e.key === activeExamKey) ?? null : null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // A failed load is not "0 certified, 0 available": without the exams there
  // is nothing to show, and without the credentials every exam would read
  // "not started" (offering Start to someone already certified).
  if (examsFailed || credentialsFailed) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-gray-900">Domain Certifications</h1>
        <LoadErrorNotice
          error={examsFailed ? examsError : credentialsError}
          onRetry={() => {
            if (examsFailed) void refetchExams();
            if (credentialsFailed) void refetchCredentials();
          }}
          failedMessage="Your certifications could not be loaded."
          testIdPrefix="certifications"
        />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Domain Certifications</h1>
        <p className="mt-1 text-gray-500">
          Take graded domain exams. A passed exam earns a domain certification, and you choose whether to show it.
        </p>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <div className="card-body text-center">
            <div className="text-3xl font-bold text-green-600">{passedCount}</div>
            <div className="text-sm text-gray-500">Certified</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-3xl font-bold text-blue-600">{inProgressCount}</div>
            <div className="text-sm text-gray-500">In Progress</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body text-center">
            <div className="text-3xl font-bold text-gray-600">{allExams?.length || 0}</div>
            <div className="text-sm text-gray-500">Available</div>
          </div>
        </div>
      </div>

      {activeExam && (
        <ActiveExamPanel
          exam={activeExam}
          scenarios={examScenarios(activeExam)}
          isLoadingScenarios={isLoadingScenarios}
          onClose={() => setActiveExamKey(null)}
        />
      )}

      {/* Category filter */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setActiveCategory('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
            activeCategory === 'all'
              ? 'bg-primary-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        {Object.entries(CATEGORY_COLORS).map(([category, colors]) => (
          <button
            key={category}
            onClick={() => setActiveCategory(category as DomainCategory)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
              activeCategory === category
                ? `${colors.bg} ${colors.text} ${colors.border} border`
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {category.replace('_', ' ')}
          </button>
        ))}
      </div>

      {/* Exam grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredExams?.map((exam) => {
          const verifiedCredential = myVerifiedCredentials.find((vc) => vc.domainKey === exam.key && vc.isActive);
          return (
            <ExamCard
              key={exam.key}
              exam={exam}
              credential={getCredential(exam.key)}
              verificationHash={verifiedCredential?.verificationHash}
              onStart={(key) => {
                const e = allExams?.find((ex) => ex.key === key);
                if (e) setSelectedExam(e);
              }}
              onContinue={handleContinueExam}
              isStarting={isStarting}
              hasScenario={isLoadingScenarios || examScenarios(exam).length > 0}
            />
          );
        })}
      </div>

      {filteredExams?.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <BookIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No certifications found in this category</p>
        </div>
      )}

      {/* Exam detail modal */}
      {selectedExam && (
        <ExamDetailModal
          exam={selectedExam}
          onClose={() => { setSelectedExam(null); setStartError(null); }}
          onStart={() => handleStartExam(selectedExam.key)}
          isStarting={isStarting || isLoadingScenarios}
          hasScenario={isLoadingScenarios || examScenarios(selectedExam).length > 0}
          startError={startError}
          engineOff={engine.off}
        />
      )}
    </div>
  );
}

// ============================================
// ACTIVE EXAM
// ============================================

/**
 * An exam attempt in progress: progress toward the definition's requirements,
 * the authored scenarios to take next, and Complete once the session and time
 * requirements are met (the server computes the score; none is sent).
 */
function ActiveExamPanel({
  exam,
  scenarios,
  isLoadingScenarios,
  onClose,
}: {
  exam: DomainExamDefinition;
  scenarios: Scenario[];
  isLoadingScenarios: boolean;
  onClose: () => void;
}) {
  const { data: progress, isError: noActiveAttempt, isFetching } = useGetMyExamProgressQuery(exam.key, {
    refetchOnMountOrArgChange: true,
  });
  const [completeExam, { isLoading: isCompleting }] = useCompleteExamMutation();
  const [completion, setCompletion] = useState<CompleteExamResponse | null>(null);
  const [completeError, setCompleteError] = useState<string | null>(null);

  const handleComplete = async () => {
    if (!progress) return;
    setCompleteError(null);
    try {
      setCompletion(await completeExam({ domainKey: exam.key, attemptId: progress.attemptId }).unwrap());
    } catch (error) {
      const code = (error as { data?: { error?: { code?: string } } })?.data?.error?.code;
      setCompleteError(
        code === 'EXAM_REQUIREMENTS_NOT_MET'
          ? 'The session and time requirements are not met yet.'
          : 'The exam could not be completed right now. Please try again.'
      );
    }
  };

  return (
    <section className="card" data-testid="active-exam">
      <div className="card-header flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">{exam.label} — exam in progress</h2>
        <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
      </div>
      <div className="card-body space-y-4">
        {completion ? (
          <div className={`rounded-lg p-4 ${completion.result.status === 'passed' ? 'bg-green-50 text-green-900' : 'bg-amber-50 text-amber-900'}`}>
            <p className="font-semibold">{completion.message}</p>
            <p className="text-sm mt-1">
              Score {completion.result.overallScore} (pass at {completion.result.passingScore}) over{' '}
              {completion.result.sessionsCompleted} graded sessions.
            </p>
          </div>
        ) : noActiveAttempt && !isFetching ? (
          <p className="text-sm text-gray-600">No exam attempt is open for this certification.</p>
        ) : (
          <>
            <ExamProgressPanel exam={exam} progress={progress ?? null} />
            {progress?.isReadyToComplete && (
              <button onClick={handleComplete} disabled={isCompleting} className="btn-primary px-4 py-2 text-sm disabled:opacity-50">
                {isCompleting ? 'Completing…' : 'Complete exam'}
              </button>
            )}
            {completeError && <p className="text-sm text-red-700" role="alert">{completeError}</p>}
            <div>
              <h3 className="text-sm font-medium text-gray-900 mb-2">Graded scenarios for this exam</h3>
              <ExamScenarioList domainKey={exam.key} scenarios={scenarios} isLoading={isLoadingScenarios} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ============================================
// ICONS
// ============================================

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function TargetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
      />
    </svg>
  );
}

function BookIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M6 18L18 6M6 6l12 12"
      />
    </svg>
  );
}
